import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Mail, MapPin } from "lucide-react";

const navLinks = [
  { label: "Startseite", path: "/" },
  { label: "Dienstleistungen", path: "/dienstleistungen" },
  { label: "Über uns", path: "/ueber-uns" },
  { label: "Kontakt", path: "/kontakt" },
];

const footerLinks = [
  { label: "Impressum", path: "/impressum" },
  { label: "Datenschutz", path: "/datenschutz" },
];

export default function Layout({ children }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100" data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
              <div className="w-8 h-8 bg-[#FF3B30] rounded-sm flex items-center justify-center">
                <span className="text-white font-bold text-sm" style={{ fontFamily: 'Plus Jakarta Sans' }}>SP</span>
              </div>
              <span className="text-[#0F172A] font-semibold text-lg tracking-tight hidden sm:block" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                ServicePoint Huwiler
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link text-sm font-medium transition-colors duration-200 ${
                    location.pathname === link.path
                      ? "text-[#FF3B30]"
                      : "text-[#0F172A] hover:text-[#FF3B30]"
                  }`}
                  data-testid={`nav-link-${link.path.replace(/\//g, '') || 'home'}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Mobile toggle */}
            <button
              className="md:hidden p-2 text-[#0F172A]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 animate-fade-in" data-testid="mobile-menu">
            <div className="px-6 py-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-3 text-base font-medium transition-colors ${
                    location.pathname === link.path
                      ? "text-[#FF3B30]"
                      : "text-[#0F172A]"
                  }`}
                  data-testid={`mobile-nav-link-${link.path.replace(/\//g, '') || 'home'}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="flex-1 pt-16 md:pt-20">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-[#0F172A] text-white" data-testid="main-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            {/* Company */}
            <div>
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-[#FF3B30] rounded-sm flex items-center justify-center">
                  <span className="text-white font-bold text-sm" style={{ fontFamily: 'Plus Jakarta Sans' }}>SP</span>
                </div>
                <span className="font-semibold text-lg tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                  ServicePoint Huwiler
                </span>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed max-w-xs">
                Digitale Lösungen für Unternehmen, E-Commerce und Gesundheitswesen. Mit Sitz in der Schweiz.
              </p>
            </div>

            {/* Navigation */}
            <div>
              <h4 className="text-sm font-semibold tracking-wide uppercase text-slate-500 mb-6" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Navigation
              </h4>
              <div className="space-y-3">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="flex items-center gap-1 text-slate-300 hover:text-white text-sm transition-colors group"
                    data-testid={`footer-link-${link.path.replace(/\//g, '') || 'home'}`}
                  >
                    {link.label}
                    <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                ))}
              </div>
            </div>

            {/* Contact & Legal */}
            <div>
              <h4 className="text-sm font-semibold tracking-wide uppercase text-slate-500 mb-6" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Kontakt & Rechtliches
              </h4>
              <div className="space-y-3 mb-6">
                <a
                  href="mailto:servicepoint.huwiler89@gmail.com"
                  className="flex items-center gap-2 text-slate-300 hover:text-white text-sm transition-colors"
                  data-testid="footer-email-link"
                >
                  <Mail size={14} />
                  servicepoint.huwiler89@gmail.com
                </a>
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <MapPin size={14} />
                  Buchrain, Schweiz
                </div>
              </div>
              <div className="flex gap-4">
                {footerLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="text-slate-500 hover:text-slate-300 text-xs transition-colors"
                    data-testid={`footer-legal-${link.path.replace(/\//g, '')}`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-xs">
              &copy; {new Date().getFullYear()} ServicePoint Huwiler. Alle Rechte vorbehalten.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
